import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, TOKEN_KEY } from "../lib/api";

const AuthContext = createContext(null);
const UNLOCK_KEY = "timbercut_unlocked";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = error, object = ready
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [pinEnabled, setPinEnabled] = useState(null); // null = unknown
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(UNLOCK_KEY) === "1");

  const autoLogin = useCallback(async () => {
    const { data } = await api.post("/auth/auto-login");
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
  }, []);

  const checkSession = useCallback(async () => {
    if (localStorage.getItem(TOKEN_KEY)) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    try {
      await autoLogin();
    } catch {
      setUser(false);
    }
  }, [autoLogin]);

  const refreshPinStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/pin-status");
      setPinEnabled(!!data.pin_enabled);
    } catch {
      setPinEnabled(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (user && typeof user === "object") refreshPinStatus();
  }, [user, refreshPinStatus]);

  const unlock = async (pin) => {
    try {
      await api.post("/auth/pin/verify", { pin });
      sessionStorage.setItem(UNLOCK_KEY, "1");
      setUnlocked(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || "Incorrect PIN." };
    }
  };

  const setPin = async (pin, currentPin) => {
    try {
      await api.post("/auth/pin/set", { pin, current_pin: currentPin });
      setPinEnabled(true);
      sessionStorage.setItem(UNLOCK_KEY, "1");
      setUnlocked(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || "Could not set PIN." };
    }
  };

  const disablePin = async (currentPin) => {
    try {
      await api.post("/auth/pin/disable", { current_pin: currentPin });
      setPinEnabled(false);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || "Could not disable PIN." };
    }
  };

  const lock = () => {
    sessionStorage.removeItem(UNLOCK_KEY);
    setUnlocked(false);
  };

  const retry = () => {
    setUser(null);
    checkSession();
  };

  const locked = pinEnabled === true && !unlocked;

  return (
    <AuthContext.Provider
      value={{ user, token, retry, pinEnabled, locked, unlock, lock, setPin, disablePin }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
